import { Table, type TableColumnsType, type TableProps } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ExportExcelButton, ColumnManagerDropdown } from '../../common';
import { useColumnManager } from '../../../hooks';
import * as XLSX from 'xlsx';

const PaginatedTableProduccion = ({ 
    csvName, 
    variables_api,
    getKgFibra, 
    getKgSemilla, 
    getAvgRindeFibra, 
    getAvgRindeSemilla,
    getCantidadFardos,
    getFullData,
    getisLoading }: any) => {

    const { user, getAccessTokenSilently } = useAuth0();
    const tipo_usuario = user?.tipo_usuario;

    // Fetch Data
    const [fullTableData, setFullTableData] = useState([]);
    const [filteredTableData, setFilteredTableData] = useState(fullTableData)
    const [loading, setLoading] = useState(false)

    // Estado controlado de filtros de columna
    const [filterState, setFilterState] = useState<Record<string, any>>({})

    // Para filtrar
    const desired_output = (data: any, column: any) => {
        const unique_values = data
            .map((item: any) => item[column])
            .filter(
                (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
            );
        return unique_values;
    };

    // Para cuando se filtre la tabla desde la tabla.
    // En lugar de modificar la tabla completa, usamos otra variable.
    const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra: any) => {
        setFilterState(filters as Record<string, any>);
        setFilteredTableData(extra.currentDataSource);
      };
      
    const apiEndpoint = `${process.env.REACT_APP_REPORTES_URL}/produccion/${variables_api}`;

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Loading es para manejar el icono de carga de la tabla.
                setLoading(true);
                const token = await getAccessTokenSilently();
                const response = await fetch(apiEndpoint, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                const data = await response.json();
                // Compartimos y guardamos info.
                setFullTableData(Array.isArray(data?.data) ? data.data : []);
                getFullData(Array.isArray(data?.data) ? data.data : []);
                getKgFibra(data?.kg_fibra || 0);
                getKgSemilla(data?.kg_semilla || 0);
                getAvgRindeFibra(data?.avg_rinde_fibra || 0);
                getAvgRindeSemilla(data?.avg_rinde_semilla || 0);
                getCantidadFardos(data?.q_fardos || 0);
                setLoading(false);
                getisLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
                getisLoading(false);
            }
        };
        if (variables_api) {
            fetchData();
        }
    }, [variables_api, apiEndpoint, getAccessTokenSilently]); 

    // Clientes únicos para el filtro — memoizados para evitar re-renders que resetean el estado de filtro de Ant Design.
    const clientesUnicos = useMemo(
        () => desired_output(fullTableData, 'cliente').map((c: any) => ({ text: c, value: c })),
        [fullTableData]
    );

    const productorUnicos = useMemo(
        () => desired_output(fullTableData, 'productor').map((p: any) => ({ text: p, value: p })),
        [fullTableData]
    );

    const establecimientosUnicos = useMemo(
        () => desired_output(fullTableData, 'establecimiento').filter((v: any) => v).map((e: any) => ({ text: e, value: e })),
        [fullTableData]
    );

    // Table
    type DataType = {
        key: React.Key
        cliente: string
        productor: string
        establecimiento: string
        numero_produccion: number
        fecha: Date
        producto: string
        kg_fibra: number
        kg_semilla: number
        cantidad_fardos: number
        duracion: number
        ritmo_desmote_kg_hora: number
        ritmo_desmote_q_hora: number
        numero_ticket: string
        numero_remito: string
        dtv: string
        compra_servicio: string
        tiene_rollos: boolean
        peso: number
        peso_bruto_fardos: number
        resto_positivo: number
        resto_negativo: number
        rinde_fibra: number
        rinde_semilla: number
      }

    const ritmoDesmoteColumns: TableColumnsType<DataType> = useMemo(() => [
        {
            title: 'Duración (H)',
            dataIndex: 'duracion',
            key: 'duracion',
            width: 110,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.duracion - b.duracion,
        },
        {
            title: 'Ritmo (Kg Fib/H)',
            dataIndex: 'ritmo_desmote_kg_hora',
            key: 'ritmo_desmote_kg_hora',
            width: 140,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.ritmo_desmote_kg_hora - b.ritmo_desmote_kg_hora,
        },
        {
            title: 'Ritmo (Q Fard/H)',
            dataIndex: 'ritmo_desmote_q_hora',
            key: 'ritmo_desmote_q_hora',
            width: 140,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.ritmo_desmote_q_hora - b.ritmo_desmote_q_hora,
        },
        {
            title: 'Con Rollo',
            dataIndex: 'tiene_rollos',
            key: 'tiene_rollos',
            width: 100,
            align: 'center',
            render: (value: boolean) => value ? 'Sí' : 'No',
        },
    ], []);

    const baseColumns: TableColumnsType<DataType> = useMemo(() => [
        {
            title: 'Cliente',
            dataIndex: 'cliente',
            key: 'cliente',
            width: 150,
            ellipsis: true,
            render: (text) => <i>{text}</i>,

            filters: clientesUnicos,
            filteredValue: filterState['cliente'] ?? null,
            onFilter: (value, record) => record.cliente === value,
        },
        {
            title: 'Productor',
            dataIndex: 'productor',
            key: 'productor',
            width: 150,
            ellipsis: true,

            filters: productorUnicos,
            filteredValue: filterState['productor'] ?? null,
            onFilter: (value, record) => record.productor === value,
        },
        {
            title: 'N° Producción',
            dataIndex: 'numero_produccion',
            key: 'numero_produccion',
            width: 120,

            align: 'right',
            render: (text) => <Link to={`/reportes/produccion/detalle/?nro_produccion=${text}`}>{text}</Link>,

        },
        {
            title: 'Fecha',
            dataIndex: 'fecha',
            key: 'fecha',
            width: 110,

        },
        {
            title: 'Producto',
            dataIndex: 'producto',
            key: 'producto',
            width: 130,
            ellipsis: true,
        },
        {
            title: 'Kg. Fibra',
            dataIndex: 'kg_fibra',
            key: 'kg_fibra',
            width: 110,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.kg_fibra - b.kg_fibra,
        },
        {
            title: 'Kg. Semilla',
            dataIndex: 'kg_semilla',
            key: 'kg_semilla',
            width: 120,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.kg_semilla - b.kg_semilla,
        },
        {
            title: 'Cant. Fardos',
            dataIndex: 'cantidad_fardos',
            key: 'cantidad_fardos',
            width: 110,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.cantidad_fardos - b.cantidad_fardos,
        },
        {
            title: 'Fardos Bruto',
            dataIndex: 'peso_bruto_fardos',
            key: 'peso_bruto_fardos',
            width: 110,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.peso_bruto_fardos - b.peso_bruto_fardos,
        },
        {
            title: 'N° Ticket',
            dataIndex: 'numero_ticket',
            key: 'numero_ticket',
            width: 140,
            ellipsis: true,
        },
        {
            title: 'Nro. Remito',
            dataIndex: 'numero_remito',
            key: 'numero_remito',
            width: 120,
            ellipsis: true,
        },
        {
            title: 'DTV',
            dataIndex: 'dtv',
            key: 'dtv',
            width: 110,
            ellipsis: true,
        },
        {
            title: 'Compra/Servicio',
            dataIndex: 'compra_servicio',
            key: 'compra_servicio',
            width: 130,
            ellipsis: true,
        },
        {
            title: 'Establecimiento',
            dataIndex: 'establecimiento',
            key: 'establecimiento',
            width: 160,
            ellipsis: true,

            filters: establecimientosUnicos,
            filteredValue: filterState['establecimiento'] ?? null,
            onFilter: (value, record) => record.establecimiento === value,
        },
        {
            title: 'Alg. Bruto',
            dataIndex: 'peso',
            key: 'peso',
            width: 110,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.peso - b.peso,
        },
        {
            title: 'Rinde Fibra',
            dataIndex: 'rinde_fibra',
            key: 'rinde_fibra',
            width: 110,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.rinde_fibra - b.rinde_fibra,
        },
        {
            title: 'Rinde Semilla',
            dataIndex: 'rinde_semilla',
            key: 'rinde_semilla',
            width: 120,

            align: 'right',
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.rinde_semilla - b.rinde_semilla,
        },
        {
            title: 'Resto (+)',
            dataIndex: 'resto_positivo',
            key: 'resto_positivo',
            width: 100,

            align: 'right',
            sorter: (a, b) => a.resto_positivo - b.resto_positivo,
        },
        {
            title: 'Resto (-)',
            dataIndex: 'resto_negativo',
            key: 'resto_negativo',
            width: 100,

            align: 'right',
            sorter: (a, b) => a.resto_negativo - b.resto_negativo,
        }
    ], [clientesUnicos, productorUnicos, establecimientosUnicos, filterState]);

    const allColumns: TableColumnsType<DataType> = useMemo(() => [
        ...baseColumns,
        ...(tipo_usuario === 'D' ? ritmoDesmoteColumns : []),
    ], [baseColumns, tipo_usuario, ritmoDesmoteColumns]);

    // Lista de todas las columnas disponibles (keys)
    const allColumnKeys = allColumns.map(col => col.key as string).filter(Boolean);

    // Nombres amigables para las columnas
    const columnNames: Record<string, string> = {
        'cliente': 'Cliente',
        'productor': 'Productor',
        'numero_produccion': 'N° Producción',
        'fecha': 'Fecha',
        'producto': 'Producto',
        'kg_fibra': 'Kg. Fibra',
        'kg_semilla': 'Kg. Semilla',
        'cantidad_fardos': 'Cant. Fardos',
        'peso_bruto_fardos': 'Fardos Bruto',
        'numero_ticket': 'N° Ticket',
        'numero_remito': 'Nro. Remito',
        'dtv': 'DTV',
        'compra_servicio': 'Compra/Servicio',
        'establecimiento': 'Establecimiento',
        'peso': 'Alg. Bruto',
        'rinde_fibra': 'Rinde Fibra',
        'rinde_semilla': 'Rinde Semilla',
        'resto_positivo': 'Resto (+)',
        'resto_negativo': 'Resto (-)',
        'duracion': 'Duración (H)',
        'ritmo_desmote_kg_hora': 'Ritmo (Kg Fib/H)',
        'ritmo_desmote_q_hora': 'Ritmo (Q Fard/H)',
        'tiene_rollos': 'Con Rollo'
    };

    // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
    // Los cambios (tildar/destildar, arrastrar) se aplican al instante, sin botón "Aplicar".
    const columnManager = useColumnManager({
        storageKey: 'reportes_produccion',
        allKeys: allColumnKeys,
    });

    // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
    const filteredColumns = useMemo(() => {
        const columnsByKey = new Map(allColumns.map(col => [col.key as string, col]));
        return columnManager.orderedVisibleKeys
            .map(key => columnsByKey.get(key))
            .filter((col): col is (typeof allColumns)[number] => Boolean(col));
    }, [columnManager.orderedVisibleKeys, allColumns]);

    const columns: TableColumnsType<DataType> = filteredColumns;

    const data: DataType[] = fullTableData.map((fila: any, index: number) => {
        return {
            // Una fila por producción: la clave ya no depende del ticket (dejaron de duplicarse).
            key: `${fila.id ?? fila.numero_produccion ?? ''}-${index}`,
            cliente: fila.cliente,
            productor: fila.productor,
            establecimiento: fila.establecimiento || '',
            numero_produccion: fila.numero_produccion,
            fecha: fila.fecha,
            producto: fila.producto || '',
            kg_fibra: parseFloat(fila.kg_fibra) || 0,
            kg_semilla: parseFloat(fila.kilos_semilla) || 0,
            cantidad_fardos: fila.cantidad_fardos,
            peso_bruto_fardos: parseFloat(fila.peso_bruto_fardos) || 0,
            duracion: fila.duracion,
            ritmo_desmote_kg_hora: parseFloat(fila.ritmo_desmote_kg_hora) || 0,
            ritmo_desmote_q_hora: parseFloat(fila.ritmo_desmote_q_hora) || 0,
            numero_ticket: fila.numero_ticket || '',
            numero_remito: fila.numero_remito || '',
            dtv: fila.dtv || '',
            compra_servicio: fila.compra_servicio || '',
            tiene_rollos: !!fila.tiene_rollos,
            peso: parseFloat(fila.peso) || 0,
            resto_positivo: parseFloat(fila.resto_positivo) || 0,
            resto_negativo: parseFloat(fila.resto_negativo) || 0,
            rinde_fibra: parseFloat(fila.rinde_fibra) || 0,
            rinde_semilla: parseFloat(fila.rinde_semilla) || 0
        }
    });

    // Función para exportar a Excel
    const handleExportExcel = () => {
        try {
            const sourceData: any[] = filteredTableData.length > 0 ? filteredTableData : data;
            const exportData = sourceData.map((row: any) => {
                const exportRow: Record<string, any> = {};
                columnManager.orderedVisibleKeys.forEach(key => {
                    exportRow[columnNames[key] || key] = row[key];
                });
                return exportRow;
            });
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Producción');
            XLSX.writeFile(workbook, csvName || `produccion_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Error al exportar Excel:', error);
        }
    };

    return (
      <div>
        <div style={{ 
            marginBottom: '16px', 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap'
        }}>
            <ColumnManagerDropdown
                manager={columnManager}
                labels={columnNames}
                buttonSize="middle"
            />
            <ExportExcelButton
                onClick={handleExportExcel}
                size="middle"
                disabled={fullTableData.length === 0}
            />
        </div>
        <Table 
            columns={columns} 
            dataSource={data}
            size="middle"
            scroll={{ x: "max-content", y: 'max(150px, calc(100vh - 400px))' }}
            onChange={onChange}
            loading={loading}
            pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomRight']
            }}
        >
        </Table>
      </div>
    );
  };
  
  export default PaginatedTableProduccion;